/**
 * Marketing tab — site-wide announcement ribbons (CRUD).
 * Owns its own data fetching off the admin API, so adding to the
 * dashboard is a single tab manifest entry.
 */
import { useEffect, useState } from "react";
import { Megaphone, Plus, Pencil, Trash } from "lucide-react";
import { toast } from "sonner";
import { AnnouncementEditModal } from "./AnnouncementEditModal";

const SEVERITY_PILL = {
  info:    "bg-[#0A192F] text-[#C5A059]",
  warning: "bg-amber-100 text-amber-800",
  urgent:  "bg-rose-100 text-rose-800",
};

export const AnnouncementsPanel = ({ api }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | <announcement object>
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/announcements");
      setItems(data.announcements || []);
    } catch {
      toast.error("Failed to load announcements.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload, id) => {
    try {
      if (id) {
        await api.put(`/admin/announcements/${id}`, payload);
        toast.success("Announcement updated.");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success("Announcement created.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success("Deleted.");
      await load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="announcements-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#0A192F]">
          <Megaphone size={18} className="text-[#C5A059]" />
          <h2 className="font-accent text-sm tracking-wider">SITE-WIDE ANNOUNCEMENTS</h2>
        </div>
        <button
          data-testid="new-announcement-btn"
          onClick={() => setEditing("new")}
          className="text-[11px] font-accent text-white bg-[#0A192F] hover:bg-[#0A192F]/90 px-3 py-2 inline-flex items-center gap-1.5"
        >
          <Plus size={12} /> NEW ANNOUNCEMENT
        </button>
      </div>

      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-5 py-8 text-center text-slate-400 text-xs">
            No announcements yet. Click <strong>NEW ANNOUNCEMENT</strong> to schedule the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500">
              <tr className="text-left">
                <th className="px-5 py-2 font-accent text-[10px]">MESSAGE</th>
                <th className="px-5 py-2 font-accent text-[10px] w-24">SEVERITY</th>
                <th className="px-5 py-2 font-accent text-[10px] w-44">WINDOW</th>
                <th className="px-5 py-2 font-accent text-[10px] w-24">STATUS</th>
                <th className="px-5 py-2 font-accent text-[10px] w-24 text-right">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`announcement-row-${a.id}`} className="border-t border-slate-100 align-top">
                  <td className="px-5 py-3 text-[#0A192F]">{a.message}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block text-[10px] font-accent tracking-wider px-2 py-0.5 ${SEVERITY_PILL[a.severity] || SEVERITY_PILL.info}`}>
                      {a.severity.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[10px] text-slate-500">
                    {a.launch_at ? <div>From: {new Date(a.launch_at).toLocaleString()}</div> : <div>Immediate</div>}
                    {a.archive_at ? <div>Until: {new Date(a.archive_at).toLocaleString()}</div> : <div>No expiry</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block text-[10px] font-accent px-2 py-0.5 ${a.is_live ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {a.is_live ? "LIVE" : (a.active ? "SCHEDULED/EXPIRED" : "OFF")}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        data-testid={`edit-announcement-${a.id}`}
                        onClick={() => setEditing(a)}
                        className="p-1.5 text-slate-500 hover:text-[#0A192F] border border-slate-200 hover:border-[#0A192F]"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        data-testid={`delete-announcement-${a.id}`}
                        onClick={() => del(a.id)}
                        className="p-1.5 text-rose-500 hover:text-rose-700 border border-slate-200 hover:border-rose-300"
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <AnnouncementEditModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing === "new" ? null : editing.id)}
        />
      )}
    </div>
  );
};
